import React, { useContext, useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  RemixBuilderContext,
  applyRemixLoading,
} from "../remix/shared/RemixBuilderContext";
import RemixBuilderSettingsTheme from "../remix/shared/RemixBuilderSettingsTheme";
import RemixBuilderSettingsBreadcrumb from "../remix/shared/RemixBuilderSettingsBreadcrumb";
import RemixModalOverlay from "../remix/shared/RemixModalOverlay";
import CFInput from "../shared/forms/CFInput";
import CFToggle from "../shared/forms/CFToggle";
import CFSelect from "../shared/forms/CFSelect";
import Tooltip from "../shared/Tooltip";
import useActionCable from "../shared/useActionCable";
import apiRequest from "../../utils/apiRequest";
import {
  canUseAiRemixPaid,
  remixAiUpgradePlan,
} from "../../utils/canUseAiRemixText";

const STEP_OPTIONS = [
  { step: 1, name: "Theme" },
  { step: 2, name: "Details" },
];

const ELEMENT_TYPE_OPTIONS = [
  { value: "headline", label: "Headlines" },
  { value: "text", label: "Text" },
  { value: "button", label: "Buttons" },
  { value: "form", label: "Forms" },
  { value: "survey", label: "Surveys" },
  { value: "quiz", label: "Quizzes" },
  { value: "list", label: "Lists" },
  { value: "collapse", label: "Collapses" },
  { value: "tabs", label: "Tabs" },
  { value: "carousel", label: "Carousels" },
  { value: "product_hero", label: "Product Heroes" },
  { value: "products", label: "Products" },
];

function extractStyleValue(val) {
  if (val == null) return null;
  if (typeof val === "object" && "value" in val) return val.value;
  if (typeof val === "object" && "hex" in val) return val.hex;
  return typeof val === "string" ? val : String(val);
}

function buildDuplicateIframeSrc(
  website,
  cta,
  variant,
  deviceType,
  remixStyles
) {
  if (!website || !cta || !variant) return "";

  const rootUrl = window.location.origin;
  let src =
    rootUrl +
    `/websites/${website.id}/cta/${cta.id}/page/?iframe=true&variant=${variant.variation}&remix=true&device_type=${deviceType}`;

  if (remixStyles) {
    const styleMap = {
      "heading-font-family": "brand_font_headline",
      "paragraph-font-family": "brand_font_paragraph",
      "heading-font-color": "heading_font_color",
      "paragraph-font-color": "paragraph_font_color",
      "button-color": "button_color",
      "button-text-color": "button_text_color",
      "accent-color": "accent_color",
      "background-color": "background_color",
    };

    Object.entries(styleMap).forEach(([remixKey, paramKey]) => {
      const val = extractStyleValue(remixStyles[remixKey]);
      if (val) {
        src += "&" + paramKey + "=" + encodeURIComponent(val);
      }
    });
  }

  return src;
}

// ─── Provider ────────────────────────────────────────────────────────────────

const DuplicateAndRemixProvider = ({ website, currentUser, children }) => {
  const initialContext = {
    current_user: currentUser,
    website,
    environment: typeof window !== "undefined" ? window.environment : undefined,
    uploaded_sections: [],
    matched_sections: [],
    section_templates: [],
    current_step: 1,
    remix_settings: {
      variant_name: "",
      styles: {},
      options: {
        remix_all_text: false,
        remix_instructions: "",
        remix_tone: "",
        excluded_element_types: [],
      },
    },
    settings: {
      max_section_remixes: 50,
      used_section_remixes: 0,
    },
    remix_mappings: {},
    remix_loading: null,
    detected_asset_theme: null,
    detected_theme: null,
  };

  const [contextData, setContextData] = useState(initialContext);

  const builder = {
    goToStep: (step) => {
      setContextData((prev) => ({ ...prev, current_step: step }));
    },
    goToPreviousStep: () => {
      setContextData((prev) => ({
        ...prev,
        current_step: Math.max(1, prev.current_step - 1),
      }));
    },
    goToNextStep: () => {
      setContextData((prev) => ({
        ...prev,
        current_step: Math.min(2, prev.current_step + 1),
      }));
    },
    updateRemixSettings: (key, value) => {
      setContextData((prev) => ({
        ...prev,
        remix_settings: {
          ...prev.remix_settings,
          [key]: value,
        },
      }));
    },
    setRemixLoading: (loadingState) => {
      setContextData((prev) => applyRemixLoading(prev, loadingState));
    },
  };

  return (
    <RemixBuilderContext.Provider
      value={[contextData, builder, setContextData]}
    >
      {children}
    </RemixBuilderContext.Provider>
  );
};

// ─── Settings Details (Step 2) ───────────────────────────────────────────────

const DuplicateAndRemixSettingsDetails = ({
  variant,
  website,
  cta,
  duplicateAndRemixPath,
  onClose,
  onSuccess,
}) => {
  const [contextData, builder] = useContext(RemixBuilderContext);
  const [loadingNextStep, setLoadingNextStep] = useState(false);
  const [cableSyncId, setCableSyncId] = useState(null);
  const [isAiRemixing, setIsAiRemixing] = useState(false);
  const [clonePercent, setClonePercent] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const currentUser = contextData.current_user;
  const remixSettings = contextData.remix_settings ?? {};
  const options = remixSettings.options ?? {
    remix_all_text: false,
    remix_instructions: "",
    remix_tone: "",
    excluded_element_types: [],
  };

  const { lastMessage: rewriteProgress } = useActionCable(
    isAiRemixing ? "ProgressValueChannel" : null,
    { id: cableSyncId },
    {
      autoDisconnect: true,
      autoDisconnectCondition: (value) => value === 100,
    }
  );

  useActionCable(
    cableSyncId && loadingNextStep
      ? "AsyncModalRequestConfirmationChannel"
      : null,
    { id: cableSyncId },
    {
      onReceived: (data) => {
        if (data?.status === "ready") {
          const newVariant = data?.object;
          const redirectUrl =
            newVariant?.id && website?.id && cta?.id
              ? `/websites/${website.id}/cta/${cta.id}/variants/${newVariant.id}/edit`
              : null;
          if (redirectUrl) {
            // Swap the loader for the complete state, then navigate immediately.
            builder.setRemixLoading({ loading: true, complete: true, redirectUrl });
            window.location.href = redirectUrl;
          } else if (onSuccess && typeof onSuccess === "function") {
            onSuccess(data);
          }
        } else if (data?.status === "progress") {
          if (typeof data.percent === "number") setClonePercent(data.percent);
        } else if (data?.status === "failed") {
          setLoadingNextStep(false);
          setIsAiRemixing(false);
          setCableSyncId(null);
          setClonePercent(null);
          builder.setRemixLoading(null);
          setErrorMessage(
            data?.error || "Failed to duplicate and remix. Please try again."
          );
        } else if (data?.status === "rate_limited") {
          setLoadingNextStep(false);
          setIsAiRemixing(false);
          setCableSyncId(null);
          setClonePercent(null);
          builder.setRemixLoading(null);
          setErrorMessage(
            data?.message || "Rate limited. Please try again later."
          );
        }
      },
      autoDisconnect: true,
      autoDisconnectCondition: (data) =>
        data?.status === "ready" || data?.status === "failed",
    }
  );

  useEffect(() => {
    if (!loadingNextStep) {
      builder.setRemixLoading(null);
      return;
    }
    const willAiRemix = options?.remix_all_text === true;
    const aiPhaseActive = willAiRemix && rewriteProgress != null;
    let progress;
    if (willAiRemix) {
      progress = rewriteProgress != null ? 30 + rewriteProgress * 0.7 : clonePercent;
    } else {
      progress = clonePercent;
    }
    builder.setRemixLoading({
      loading: true,
      progress,
      isAiRemixing: aiPhaseActive,
    });
  }, [loadingNextStep, rewriteProgress, clonePercent, isAiRemixing]);

  const buildRemixParams = () => {
    const rawExcluded = options.excluded_element_types;
    const excluded_element_types = Array.isArray(rawExcluded)
      ? rawExcluded
          .map((o) => (typeof o === "string" ? o : o?.value))
          .filter(Boolean)
      : [];

    return {
      remix_styles: "true",
      remix_all_text: options.remix_all_text === true ? "true" : "false",
      remix_tone: options.remix_tone || "",
      remix_instructions: options.remix_instructions || "",
      excluded_element_types,
      "heading-font-family":
        extractStyleValue(remixSettings.styles?.["heading-font-family"]) || "",
      "heading-font-color":
        extractStyleValue(remixSettings.styles?.["heading-font-color"]) || "",
      "paragraph-font-family":
        extractStyleValue(remixSettings.styles?.["paragraph-font-family"]) ||
        "",
      "paragraph-font-color":
        extractStyleValue(remixSettings.styles?.["paragraph-font-color"]) || "",
      "background-color":
        extractStyleValue(remixSettings.styles?.["background-color"]) || "",
      "button-color":
        extractStyleValue(remixSettings.styles?.["button-color"]) || "",
      "accent-color":
        extractStyleValue(remixSettings.styles?.["accent-color"]) || "",
    };
  };

  const handleSubmit = async (e) => {
    if (loadingNextStep || !duplicateAndRemixPath) return;

    const canSubmit = canUseAiRemixPaid({
      currentUser,
      website,
      subscription: contextData.subscription,
    });

    if (!canSubmit) {
      if (typeof window.subscriptions?.upgrade_modal === "function") {
        window.subscriptions.upgrade_modal(
          e ?? null,
          false,
          "use remix AI to duplicate and remix variants",
          remixAiUpgradePlan({
            subscription: contextData.subscription,
            website,
          }),
          "Triggered feature CTA – Remix AI",
          false,
          "x3rs6cvrwi"
        );
      }
      return;
    }

    setLoadingNextStep(true);
    setErrorMessage(null);
    setClonePercent(null);

    const willAiRemix = options.remix_all_text === true;

    const syncId = Math.random().toString(36).substring(2, 15);
    setCableSyncId(syncId);
    if (willAiRemix) {
      setIsAiRemixing(true);
    }

    const remixParams = buildRemixParams();

    try {
      const response = await apiRequest("POST", duplicateAndRemixPath, {
        id: variant.id,
        cable_sync_id: syncId,
        variant_name: remixSettings.variant_name || "",
        remix: remixParams,
      });

      if (response?.code === 403 || response?.json?.error) {
        setLoadingNextStep(false);
        setIsAiRemixing(false);
        setCableSyncId(null);
        builder.setRemixLoading(null);
        setErrorMessage(
          response?.json?.error || "Failed to start. Please try again."
        );
      }
    } catch (err) {
      setLoadingNextStep(false);
      setIsAiRemixing(false);
      setCableSyncId(null);
      builder.setRemixLoading(null);
      setErrorMessage("Failed to start. Please try again.");
    }
  };

  const handlePreviousStep = () => {
    if (loadingNextStep) return;
    setErrorMessage(null);
    builder.goToPreviousStep();
  };

  return (
    <React.Fragment>
      <div className="remix-editor-settings-layouts flex flex-column overflow-scroll grow-1 min-w-0">
        <h2 className="text-[18px] font-semibold mt-15">
          Duplicate & remix variant
        </h2>
        <p className="text-[12px] text-gray-400">
          Name your new variant and configure AI text remixing
        </p>

        <div className="min-w-0 mt-5">
          <div className="mb-[5px] col-xs-9 no-padding-sides">
            <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap">
              Variant name
            </span>
          </div>

          <CFInput
            name="variant_name"
            placeholder={variant?.name || "New variant name"}
            value={remixSettings.variant_name ?? ""}
            onChange={(value) => {
              builder.updateRemixSettings("variant_name", value);
            }}
          />
        </div>

        <div className="min-w-0 mt-5">
          <div className="flex items-center gap-2">
            <span className="setting-label text-xl text-black dark:text-white">
              AI Remix
            </span>

            <Tooltip
              tooltip={`Toggle this to use the remix AI instructions<br> to AI remix all text in the variant.`}
            />

            <span className="bg-blue-600 py-1 px-2.5 rounded text-sm text-white font-medium">
              BETA
            </span>
          </div>

          <div className="mt-2 row hard-center pl-[20px]">
            <CFToggle
              name="remix_all_text"
              checked={options.remix_all_text === true}
              onChange={(checked, e) => {
                const isEnabled = canUseAiRemixPaid({
                  currentUser,
                  website,
                  subscription: contextData.subscription,
                });

                if (!isEnabled) {
                  if (checked) {
                    if (
                      typeof window.subscriptions?.upgrade_modal === "function"
                    ) {
                      window.subscriptions.upgrade_modal(
                        e ?? null,
                        false,
                        "use remix AI to generate a template",
                        remixAiUpgradePlan({
                          subscription: contextData.subscription,
                          website,
                        }),
                        "Triggered feature CTA – Remix AI",
                        false,
                        "x3rs6cvrwi"
                      );
                    }
                  } else {
                    builder.updateRemixSettings("options", {
                      ...options,
                      remix_all_text: false,
                    });
                  }
                  return;
                }

                builder.updateRemixSettings("options", {
                  ...options,
                  remix_all_text: checked,
                });
              }}
            />
          </div>
        </div>

        {options.remix_all_text === true && (
          <div className="min-w-0 mt-5">
            <div className="mb-[5px] col-xs-9 no-padding-sides">
              <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap text-left">
                AI Instructions For New Variant{" "}
                <Tooltip tooltip="This will look for specific parts of the campaign to modify based on your instructions." />
              </span>
            </div>

            <textarea
              name="remix_instructions"
              rows={4}
              placeholder="e.g. Change 10% discount to 15% discount"
              value={options.remix_instructions ?? ""}
              onChange={(e) => {
                builder.updateRemixSettings("options", {
                  ...options,
                  remix_instructions: e.target.value,
                });
              }}
              className="w-full p-3 bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0 appearance-none resize-y min-h-[4rem]"
            />
          </div>
        )}

        {options.remix_all_text === true && (
          <div className="min-w-0 mt-5">
            <div className="mb-[5px] col-xs-9 no-padding-sides">
              <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap text-left">
                Instructions To Rewrite All Copy (optional){" "}
                <Tooltip tooltip="This will go through all text and rewrite it based on your instructions." />
              </span>
            </div>

            <textarea
              name="remix_tone"
              rows={3}
              placeholder="e.g. More urgency for Black Friday"
              value={options.remix_tone ?? ""}
              onChange={(e) => {
                builder.updateRemixSettings("options", {
                  ...options,
                  remix_tone: e.target.value,
                });
              }}
              className="w-full p-3 bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0 appearance-none resize-y min-h-[4rem]"
            />
          </div>
        )}

        {options.remix_all_text === true && (
          <div className="min-w-0 mt-5">
            <div className="mb-[5px] col-xs-9 no-padding-sides">
              <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap text-left">
                Exclude Elements From Remixing{" "}
                <Tooltip tooltip="Select element types to exclude from AI text remixing." />
              </span>
            </div>

            <CFSelect
              options={ELEMENT_TYPE_OPTIONS}
              value={options.excluded_element_types ?? []}
              onChange={(selected) => {
                builder.updateRemixSettings("options", {
                  ...options,
                  excluded_element_types: selected ?? [],
                });
              }}
              isMulti={true}
              placeholder="Select types to exclude..."
              isClearable={true}
            />
          </div>
        )}
      </div>

      {errorMessage && (
        <div
          className="col-xs-12 p-4 mb-4 rounded-md bg-red-50 border border-red-200 text-red-800"
          role="alert"
        >
          {errorMessage}
        </div>
      )}

      <div className="remix-editor-settings-buttons pt-[30px] pb-[30px] gap-4 flex justify-between">
        <div
          onClick={handlePreviousStep}
          className={`cursor-pointer rounded-xl w-full p-[10px] flex justify-center items-center text-xl border border-solid border-slate-200 bg-white text-black ${
            loadingNextStep ? "disabled" : ""
          }`}
        >
          Back
        </div>
        {(() => {
          const willShowPurple = options.remix_all_text === true;
          const usePurpleButton = willShowPurple || isAiRemixing;
          return (
            <div
              onClick={handleSubmit}
              className={`cursor-pointer rounded-xl w-full p-[10px] flex justify-center items-center text-xl text-white ${
                loadingNextStep ? "disabled" : ""
              } ${
                usePurpleButton
                  ? "border-0"
                  : "border border-solid border-blue-700 bg-blue-600"
              }`}
              style={
                usePurpleButton
                  ? {
                      background:
                        "linear-gradient(135deg, #4f6fff 0%, #003FFF 100%)",
                    }
                  : {}
              }
            >
              {loadingNextStep ? (
                <span className="flex items-center gap-2">
                  <span className="cf-loading-spinner" />
                  {isAiRemixing && (
                    <span>
                      {rewriteProgress !== null
                        ? `Remixing... ${rewriteProgress}%`
                        : "Remixing..."}
                    </span>
                  )}
                </span>
              ) : usePurpleButton ? (
                "Remix Template"
              ) : (
                "Duplicate & Remix +"
              )}
            </div>
          );
        })()}
      </div>
    </React.Fragment>
  );
};

// ─── Modal Inner ─────────────────────────────────────────────────────────────

const DuplicateAndRemixModalInner = ({
  variant,
  website,
  cta,
  duplicateAndRemixPath,
  onClose,
  onSuccess,
  initialRemixInstructions,
}) => {
  const modalRef = useRef(null);
  const iframeContainerRef = useRef(null);
  const iframeRef = useRef(null);
  const iframeWrapperRef = useRef(null);
  const [contextData, builder] = useContext(RemixBuilderContext);
  const [deviceType, setDeviceType] = useState("desktop");
  const [fitScale, setFitScale] = useState(0.9);
  const [mobileScale, setMobileScale] = useState(0.9);
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (!initialRemixInstructions || !builder) return;
    builder.updateRemixSettings("options", {
      ...(contextData?.remix_settings?.options || {}),
      remix_all_text: true,
      remix_instructions: initialRemixInstructions.remix_instructions || "",
    });
    if (initialRemixInstructions.variant_name) {
      builder.updateRemixSettings(
        "variant_name",
        initialRemixInstructions.variant_name
      );
    }
    builder.goToStep(2);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const iframeSrc = useMemo(
    () =>
      buildDuplicateIframeSrc(
        website,
        cta,
        variant,
        deviceType,
        contextData?.remix_settings?.styles
      ),
    [website, cta, variant, deviceType, contextData?.remix_settings?.styles]
  );

  useEffect(() => {
    if (!iframeContainerRef.current) return;
    const updateDimensions = () => {
      if (!iframeContainerRef.current) return;
      const { clientWidth, clientHeight } = iframeContainerRef.current;
      if (clientWidth <= 0 || clientHeight <= 0) return;
      setContainerDimensions((prev) =>
        prev.width === clientWidth && prev.height === clientHeight
          ? prev
          : { width: clientWidth, height: clientHeight }
      );
      if (deviceType === "desktop") {
        const scaleX = clientWidth / 1332;
        const fitScaleValue = Math.max(0.5, Math.min(scaleX * 0.98, 1.5));
        setFitScale((prev) => (prev === fitScaleValue ? prev : fitScaleValue));
      }
      if (deviceType === "mobile") {
        const scaleX = clientWidth / 375;
        const scaleY = clientHeight / 667;
        const mobileScaleValue = Math.max(0.5, Math.min(Math.min(scaleX, scaleY) * 0.98, 1));
        setMobileScale((prev) => (prev === mobileScaleValue ? prev : mobileScaleValue));
      }
    };
    updateDimensions();
    let frameId = null;
    const handleResize = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        updateDimensions();
      });
    };
    const observer = new ResizeObserver(handleResize);
    observer.observe(iframeContainerRef.current);
    const timeoutId = setTimeout(updateDimensions, 100);
    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [deviceType]);

  useEffect(() => {
    const container = iframeContainerRef.current;
    const iframe = iframeRef.current;
    if (!container || !iframe) return;

    const isDesktop = deviceType === "desktop";
    const isMobile = deviceType === "mobile";

    const updateIframeDimensions = () => {
      const height = container.clientHeight;
      const width = container.clientWidth;
      if (height <= 0 || width <= 0) return;

      if (isDesktop) {
        const scaleX = width / 1332;
        const fitScaleVal = Math.max(0.5, Math.min(scaleX * 0.98, 1.5));
        const dynamicHeight = Math.max(672, Math.round(height / fitScaleVal));
        iframe.style.width = "1332px";
        iframe.style.height = `${dynamicHeight}px`;
        iframe.style.transform = `scale(${fitScaleVal})`;
        iframe.style.transformOrigin = "top left";
      } else if (isMobile) {
        const scaleX = width / 375;
        const scaleY = height / 667;
        const scale = Math.min(scaleX, scaleY) * 0.98;
        const mobileScaleVal = Math.max(0.5, Math.min(scale, 1));
        iframe.style.width = "375px";
        iframe.style.height = "667px";
        iframe.style.transform = `scale(${mobileScaleVal})`;
        iframe.style.transformOrigin = "top left";
      } else {
        iframe.style.width = `${width}px`;
        iframe.style.height = `${height}px`;
        iframe.style.transform = "";
      }
    };

    const runAfterLayout = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(updateIframeDimensions);
      });
    };

    updateIframeDimensions();
    runAfterLayout();
    const observer = new ResizeObserver(runAfterLayout);
    observer.observe(container);
    const timeoutIds = [100, 300, 600].map((ms) =>
      setTimeout(updateIframeDimensions, ms)
    );
    return () => {
      observer.disconnect();
      timeoutIds.forEach(clearTimeout);
    };
  }, [deviceType, iframeSrc]);

  useEffect(() => {
    if (modalRef.current) {
      $(modalRef.current).addClass("in").css("display", "block");
      $("body").addClass("modal-open");
    }
    return () => {
      if (modalRef.current) {
        $(modalRef.current).removeClass("in").css("display", "none");
      }
      $("body").removeClass("modal-open");
    };
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const desktopIframeHeight =
    containerDimensions.height > 0 && fitScale > 0
      ? Math.max(672, Math.round(containerDimensions.height / fitScale))
      : 672;

  return (
    <div
      ref={modalRef}
      className="cf-template-preview-modal remix-editor modal fade"
      tabIndex="-1"
      role="dialog"
      aria-labelledby="duplicateAndRemixModalLabel"
      aria-modal="true"
      data-backdrop="false"
      style={{
        background: "rgb(0, 0, 0, 0.5)",
        overflowY: "auto",
        zIndex: 10001,
      }}
      onClick={handleBackdropClick}
    >
      <div
        className="modal-dialog !w-[100%] !max-w-[1600px] !h-[100%] ml-auto mr-auto"
        role="document"
      >
        <div className="modal-content hard-center bg-white dark:bg-slate-950 !shadow-2xl max-h-[95vh] flex flex-column overflow-x-hidden">
          <div className="modal-header !border-slate-200 dark:!border-slate-800">
            <button
              type="button"
              className="close dark:text-white"
              onClick={onClose}
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 17 17"
                fill="none"
              >
                <path
                  d="M12.2996 4.1001L4.09961 12.3001M12.2996 12.3001L4.09961 4.1001"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="min-w-0 flex-1">
                <h4 className="text-black dark:text-white text-[18px] font-semibold m-0 truncate">
                  Duplicate & Remix
                </h4>
                <p className="text-slate-500 text-xl m-0">
                  {variant?.name || "Variant"}
                </p>
              </div>
            </div>
          </div>

          <div className="modal-body flex h-[85vh] flex-1 min-h-0 overflow-x-hidden relative">
            {contextData.remix_loading?.loading && (
              <div
                className="absolute inset-0 z-10 flex items-center justify-center bg-white dark:bg-slate-950"
                aria-hidden="false"
              >
                <RemixModalOverlay
                  remixLoading={contextData.remix_loading}
                  defaultStatusMessage="Duplicating and remixing..."
                />
              </div>
            )}
            <div className="flex gap-5 mt-5 px-7 flex-1 min-w-0 min-h-0 overflow-x-hidden">
              <div className="flex-1 flex flex-column min-w-0 min-h-0 max-w-[1205px]">
                <div
                  className="flex-1 flex flex-column items-start overflow-hidden bg-slate-100 rounded-xl min-h-0 w-full"
                  style={{
                    minHeight: 400,
                    paddingTop: 40,
                    paddingBottom: 24,
                    paddingLeft: 40,
                    paddingRight: 40,
                  }}
                >
                  <div
                    ref={iframeContainerRef}
                    className="relative flex-1 w-full min-h-0 flex justify-center items-start overflow-hidden"
                  >
                    {iframeSrc && (
                      <>
                        {deviceType === "desktop" ? (
                          <div
                            ref={iframeWrapperRef}
                            className="absolute inset-0 flex justify-center items-start overflow-hidden"
                          >
                            <div
                              style={{
                                width: 1332 * fitScale,
                                height: "100%",
                                overflow: "hidden",
                                flexShrink: 0,
                              }}
                            >
                              <iframe
                                ref={iframeRef}
                                id="duplicate-remix-preview-iframe"
                                src={iframeSrc}
                                title="Variant preview"
                                frameBorder="0"
                                width={1332}
                                height={desktopIframeHeight}
                                style={{ transformOrigin: "top left" }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div
                            ref={iframeWrapperRef}
                            className="absolute inset-0 flex justify-center items-center overflow-hidden"
                          >
                            <div
                              className="rounded-[2rem] overflow-hidden border-4 border-slate-700 bg-slate-800 shadow-xl"
                              style={{
                                width: 375 * mobileScale,
                                height: 667 * mobileScale,
                                flexShrink: 0,
                              }}
                            >
                              <iframe
                                ref={iframeRef}
                                id="duplicate-remix-preview-iframe"
                                src={iframeSrc}
                                title="Variant preview"
                                frameBorder="0"
                                width={375}
                                height={667}
                                style={{
                                  transform: `scale(${mobileScale})`,
                                  transformOrigin: "top left",
                                  width: 375,
                                  height: 667,
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {!contextData.remix_loading?.loading && (
                      <div
                        className="absolute left-3 flex rounded-[3px] overflow-hidden border-solid border-[1px] border-[#e6eff7] bg-white/80 shadow-sm z-10"
                        style={{ width: 100, height: 40, bottom: 22 }}
                      >
                        <button
                          type="button"
                          className={`flex-1 flex items-center justify-center h-full cursor-pointer border-r border-solid border-[1px] border-[#e6eff7] ${
                            deviceType === "desktop"
                              ? "bg-[#e6eff7] text-slate-700"
                              : "bg-transparent text-slate-600 hover:bg-slate-50"
                          }`}
                          onClick={() => setDeviceType("desktop")}
                          title="Desktop"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect
                              x="2"
                              y="3"
                              width="20"
                              height="14"
                              rx="2"
                              ry="2"
                            />
                            <line x1="8" y1="21" x2="16" y2="21" />
                            <line x1="12" y1="17" x2="12" y2="21" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className={`flex-1 flex items-center justify-center h-full cursor-pointer border-solid border-[1px] border-transparent ${
                            deviceType === "mobile"
                              ? "bg-[#e6eff7] text-slate-700"
                              : "bg-transparent text-slate-600 hover:bg-slate-50"
                          }`}
                          onClick={() => setDeviceType("mobile")}
                          title="Mobile"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect
                              x="5"
                              y="2"
                              width="14"
                              height="20"
                              rx="2"
                              ry="2"
                            />
                            <line x1="12" y1="18" x2="12.01" y2="18" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="w-[400px] shrink-0 flex flex-column justify-between min-w-0">
                <RemixBuilderSettingsBreadcrumb
                  options={STEP_OPTIONS}
                  appendLabel="Duplicate"
                />

                {contextData.current_step === 1 && (
                  <RemixBuilderSettingsTheme />
                )}
                {contextData.current_step === 2 && (
                  <DuplicateAndRemixSettingsDetails
                    variant={variant}
                    website={website}
                    cta={cta}
                    duplicateAndRemixPath={duplicateAndRemixPath}
                    onClose={onClose}
                    onSuccess={onSuccess}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Exported Modal ──────────────────────────────────────────────────────────

const DuplicateAndRemixModal = ({
  isOpen,
  onClose,
  variant,
  website,
  cta,
  duplicateAndRemixPath,
  onSuccess,
  currentUser,
  initialRemixInstructions,
}) => {
  if (!isOpen) return null;

  return createPortal(
    <DuplicateAndRemixProvider website={website} currentUser={currentUser}>
      <DuplicateAndRemixModalInner
        variant={variant}
        website={website}
        cta={cta}
        duplicateAndRemixPath={duplicateAndRemixPath}
        onClose={onClose}
        onSuccess={onSuccess}
        initialRemixInstructions={initialRemixInstructions}
      />
    </DuplicateAndRemixProvider>,
    document.body
  );
};

export default DuplicateAndRemixModal;
